/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'
import { X, Search, Star, GitFork, ExternalLink, Download, AlertCircle } from 'lucide-react'
import { getApiKey } from '@/lib/api-keys'

interface GitHubRepo {
  id: number
  name: string
  full_name: string
  description: string | null
  private: boolean
  html_url: string
  clone_url: string
  language: string | null
  stargazers_count: number
  forks_count: number
  updated_at: string
  size: number
}

interface GitHubRepoBrowserProps {
  isOpen: boolean
  onClose: () => void
  onSelectRepo: (repoUrl: string, repoName: string) => Promise<void>
}

export function GitHubRepoBrowser({ isOpen, onClose, onSelectRepo }: GitHubRepoBrowserProps) {
  const [repos, setRepos] = useState<GitHubRepo[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [username, setUsername] = useState('')
  const [activeTab, setActiveTab] = useState<'repos' | 'url'>('repos')
  const [repoUrl, setRepoUrl] = useState('')
  const [cloning, setCloning] = useState(false)

  useEffect(() => {
    if (isOpen) {
      const githubToken = getApiKey('github')
      if (githubToken && activeTab === 'repos') {
        fetchRepos(githubToken)
      }
    }
  }, [isOpen, activeTab])

  const fetchRepos = async (githubToken: string) => {
    setLoading(true)
    setError(null)

    try {
      // Fetch user info first
      const userResponse = await fetch('https://api.github.com/user', {
        headers: {
          'Authorization': `Bearer ${githubToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      })

      if (!userResponse.ok) {
        throw new Error('Invalid GitHub token. Please check your settings.')
      }

      const userData = await userResponse.json()
      setUsername(userData.login)

      // Fetch user's repositories
      const reposResponse = await fetch('https://api.github.com/user/repos?per_page=100&sort=updated', {
        headers: {
          'Authorization': `Bearer ${githubToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      })

      if (!reposResponse.ok) {
        throw new Error('Failed to fetch repositories')
      }

      const reposData = await reposResponse.json()
      setRepos(reposData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch repositories')
    } finally {
      setLoading(false)
    }
  }

  const handleCloneFromUrl = async () => {
    if (!repoUrl.trim()) {
      setError('Please enter a repository URL')
      return
    }

    // Extract repo name from URL
    const match = repoUrl.match(/github\.com\/([^/]+\/[^/]+?)(?:\.git)?$/)
    if (!match) {
      setError('Invalid GitHub URL. Expected format: https://github.com/owner/repo')
      return
    }

    const repoName = match[1]
    setCloning(true)
    setError(null)

    try {
      await onSelectRepo(repoUrl, repoName)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clone repository')
    } finally {
      setCloning(false)
    }
  }

  const handleSelectRepo = async (repo: GitHubRepo) => {
    setCloning(true)
    setError(null)

    try {
      await onSelectRepo(repo.clone_url, repo.full_name)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clone repository')
    } finally {
      setCloning(false)
    }
  }

  const filteredRepos = repos.filter(repo =>
    repo.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    repo.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (!isOpen) return null

  const githubToken = getApiKey('github')

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-background-secondary border border-border rounded-lg w-[800px] max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-condensed font-semibold">Open GitHub Repository</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-background-tertiary rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('repos')}
            className={`px-4 py-2 text-sm font-condensed transition-colors ${
              activeTab === 'repos'
                ? 'border-b-2 border-accent text-accent'
                : 'text-foreground-secondary hover:text-foreground'
            }`}
          >
            My Repositories
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`px-4 py-2 text-sm font-condensed transition-colors ${
              activeTab === 'url'
                ? 'border-b-2 border-accent text-accent'
                : 'text-foreground-secondary hover:text-foreground'
            }`}
          >
            Clone from URL
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!githubToken && (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <AlertCircle className="w-12 h-12 text-foreground-muted mb-4" />
              <h3 className="text-lg font-condensed font-semibold mb-2">GitHub PAT Required</h3>
              <p className="text-sm text-foreground-secondary mb-4">
                Please add your GitHub Personal Access Token in Settings to browse repositories.
              </p>
              <button
                onClick={onClose}
                className="btn-primary"
              >
                Go to Settings
              </button>
            </div>
          )}

          {githubToken && activeTab === 'repos' && (
            <>
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground-muted" />
                  <input
                    type="text"
                    placeholder="Search repositories..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input w-full pl-10"
                  />
                </div>
              </div>

              {/* Loading */}
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-4 mb-4">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              {/* Repositories List */}
              {!loading && !error && (
                <div className="space-y-2">
                  {filteredRepos.length === 0 ? (
                    <p className="text-sm text-foreground-muted text-center py-8">
                      {searchQuery ? 'No repositories found matching your search.' : 'No repositories found.'}
                    </p>
                  ) : (
                    filteredRepos.map(repo => (
                      <div
                        key={repo.id}
                        className="card p-4 hover:border-accent/50 transition-colors cursor-pointer"
                        onClick={() => handleSelectRepo(repo)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-condensed font-semibold text-sm mb-1">
                              {repo.name}
                              {repo.private && (
                                <span className="ml-2 text-xs text-foreground-muted">(Private)</span>
                              )}
                            </h3>
                            {repo.description && (
                              <p className="text-xs text-foreground-secondary mb-2">{repo.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-foreground-muted">
                              {repo.language && (
                                <span>{repo.language}</span>
                              )}
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3" />
                                {repo.stargazers_count}
                              </span>
                              <span className="flex items-center gap-1">
                                <GitFork className="w-3 h-3" />
                                {repo.forks_count}
                              </span>
                            </div>
                          </div>
                          <a
                            href={repo.html_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 hover:bg-background-tertiary rounded transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {githubToken && activeTab === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-condensed font-semibold mb-2">
                  Repository URL
                </label>
                <input
                  type="text"
                  placeholder="https://github.com/owner/repo"
                  value={repoUrl}
                  onChange={(e) => setRepoUrl(e.target.value)}
                  className="input w-full"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCloneFromUrl()
                    }
                  }}
                />
                <p className="text-xs text-foreground-muted mt-1">
                  Enter a GitHub repository URL to clone
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-4">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={handleCloneFromUrl}
                disabled={cloning}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cloning ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Cloning...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Clone Repository
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

